import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import YouTube from 'react-youtube';
import { X, Play, Pause, SkipForward, Trash2, Volume2, VolumeX, Repeat, Shuffle, QrCode, Music, Plus } from 'lucide-react';
import MusicQRModal from './MusicQRModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MusicControlModal({ isOpen, onClose }) {
  const [queue, setQueue] = useState([]);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(80);
  const [loopMode, setLoopMode] = useState(false);
  const [shuffleMode, setShuffleMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showQRModal, setShowQRModal] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [newSongUrl, setNewSongUrl] = useState('');
  const [addingUrl, setAddingUrl] = useState(false);
  
  const playerRef = useRef(null);
  const socketRef = useRef(null);

  // Global error handler for this component
  useEffect(() => {
    const handleError = (event) => {
      console.error('[MUSIC] Global error caught:', event.error);
      event.preventDefault(); // Prevent default error handling
    };

    const handleUnhandledRejection = (event) => {
      console.error('[MUSIC] Unhandled promise rejection:', event.reason);
      event.preventDefault();
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Play next song
  const playNextSong = useCallback(async () => {
    try {
      console.log('[MUSIC] Playing next song...');
      
      const response = await axios.put(`${API}/music/status`, null, {
        params: { action: 'play_next' },
        withCredentials: true
      });

      if (response.data.now_playing) {
        console.log('[MUSIC] Now playing:', response.data.now_playing.title);
        setNowPlaying(response.data.now_playing);
        return response.data.now_playing;
      } else {
        console.log('[MUSIC] No more songs in queue');
        setNowPlaying(null);
        setIsPlaying(false);
        return null;
      }
    } catch (error) {
      console.error('[MUSIC] Failed to play next:', error);
      return null;
    }
  }, []);

  // YouTube Player Event Handlers
  const onPlayerReady = useCallback((event) => {
    try {
      console.log('[MUSIC] YouTube player ready');
      playerRef.current = event.target;
      
      // Safely start muted to allow autoplay (browser policy)
      try {
        event.target.mute();
        event.target.playVideo();
        
        // After 500ms, unmute and set volume
        setTimeout(() => {
          try {
            if (playerRef.current) {
              playerRef.current.unMute();
              playerRef.current.setVolume(volume);
              console.log('[MUSIC] Player unmuted and playing');
            }
          } catch (volumeError) {
            console.warn('[MUSIC] Volume setting failed:', volumeError);
          }
        }, 500);
        
        setIsPlaying(true);
      } catch (playError) {
        console.warn('[MUSIC] Autoplay failed:', playError);
        // Don't throw - just log
      }
    } catch (error) {
      console.error('[MUSIC] Player ready error:', error);
      // Prevent error from bubbling up
    }
  }, [volume]);

  const onPlayerStateChange = useCallback(async (event) => {
    try {
      // YouTube player states: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (cued)
      console.log('[MUSIC] Player state:', event.data);
      
      if (event.data === 0) {
        // Video ended
        console.log('[MUSIC] Video ended, auto-advancing...');
        if (loopMode && nowPlaying) {
          // Replay same video
          try {
            event.target.playVideo();
          } catch (replayError) {
            console.warn('[MUSIC] Replay failed:', replayError);
          }
        } else {
          // Play next song
          try {
            const next = await playNextSong();
            if (!next) {
              setIsPlaying(false);
            }
          } catch (nextError) {
            console.error('[MUSIC] Next song failed:', nextError);
            setIsPlaying(false);
          }
        }
      } else if (event.data === 1) {
        // Playing
        setIsPlaying(true);
      } else if (event.data === 2) {
        // Paused
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('[MUSIC] State change error:', error);
      // Don't throw - prevent error banner
    }
  }, [loopMode, nowPlaying, playNextSong]);

  const onPlayerError = useCallback((event) => {
    try {
      console.error('[MUSIC] Player error:', event.data);
      // Skip to next song on error
      playNextSong().catch(err => console.error('[MUSIC] Skip after error failed:', err));
    } catch (error) {
      console.error('[MUSIC] Error handler failed:', error);
      // Silently fail
    }
  }, [playNextSong]);

  // Fetch queue from backend
  const fetchQueue = useCallback(async () => {
    try {
      // Try auth endpoint first, fallback to public for testing
      let response;
      try {
        response = await axios.get(`${API}/music/queue`, { withCredentials: true });
      } catch (authError) {
        response = await axios.get(`${API}/music/queue-test`);
      }
      
      const queueData = response.data.queue || [];
      setQueue(queueData);
      
      // Get player state from backend
      const playerState = response.data.player_state || {};
      if (typeof playerState.is_playing === 'boolean') {
        setIsPlaying(playerState.is_playing);
      }
      
      // Find currently playing song
      const playing = queueData.find(item => item.status === 'playing');
      
      if (playing) {
        // Check if video changed - always update to ensure re-render
        setNowPlaying(prev => {
          if (!prev || prev.video_id !== playing.video_id) {
            console.log('[MUSIC REFRESH] New song detected:', playing.title);
            // Return new object to trigger re-render
            return { ...playing };
          }
          return prev; // Keep same reference to avoid unnecessary re-render
        });
        return { playing, queueData };
      } else {
        // No song playing
        setNowPlaying(prev => {
          if (prev) {
            console.log('[MUSIC REFRESH] Clearing player');
            return null;
          }
          return prev;
        });
      }
      
      return { playing: null, queueData };
    } catch (error) {
      console.error('[MUSIC] Failed to fetch queue:', error);
      return { playing: null, queueData: [] };
    }
  }, []); // No dependencies to avoid re-creating on every render

  // Initial load and auto-play first song
  useEffect(() => {
    if (!isOpen) return;

    const initAndAutoPlay = async () => {
      setLoading(true);
      const { playing, queueData } = await fetchQueue();
      
      if (playing) {
        // There's already a song playing
        console.log('[MUSIC] Found playing song:', playing.title);
        setNowPlaying(playing);
      } else if (queueData.length > 0) {
        // No song playing but queue has songs - auto-play first
        console.log('[MUSIC] Auto-playing first song from queue');
        await playNextSong();
        fetchQueue();
      }
      
      setLoading(false);
    };

    initAndAutoPlay();

    // Auto-refresh queue every 1 second to detect changes from other devices
    const refreshInterval = setInterval(() => {
      fetchQueue();
    }, 1000); // 1 second for near real-time sync

    return () => {
      clearInterval(refreshInterval);
    };
  }, [isOpen, fetchQueue, playNextSong]);

  // Socket.IO real-time updates
  useEffect(() => {
    if (!isOpen) return;

    const socket = io(BACKEND_URL, {
      transports: ['websocket', 'polling']
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[MUSIC] Socket connected');
    });

    socket.on('music_added', async (data) => {
      console.log('[MUSIC] New song added:', data);
      await fetchQueue();
      
      // If no song is currently playing, auto-play the new song
      if (!nowPlaying) {
        console.log('[MUSIC] Auto-playing newly added song');
        await playNextSong();
        await fetchQueue();
      }
    });

    socket.on('music_removed', (data) => {
      console.log('[MUSIC] Song removed:', data);
      fetchQueue();
    });

    socket.on('music_status_changed', (data) => {
      console.log('[MUSIC] Status changed:', data);
      if (data.now_playing) {
        setNowPlaying(data.now_playing);
      }
      fetchQueue();
    });

    return () => {
      socket.disconnect();
    };
  }, [isOpen, fetchQueue, playNextSong, nowPlaying]);

  const handlePlayPause = async () => {
    if (!playerRef.current) {
      console.warn('[MUSIC] Player not ready');
      return;
    }

    try {
      if (isPlaying) {
        console.log('[MUSIC] Pausing...');
        playerRef.current.pauseVideo();
        // Update backend state
        await axios.post(`${API}/music/player-state`, { is_playing: false }, { withCredentials: true })
          .catch(() => axios.post(`${API}/music/player-state-test`, { is_playing: false }));
      } else {
        console.log('[MUSIC] Playing...');
        playerRef.current.playVideo();
        // Update backend state
        await axios.post(`${API}/music/player-state`, { is_playing: true }, { withCredentials: true })
          .catch(() => axios.post(`${API}/music/player-state-test`, { is_playing: true }));
      }
    } catch (error) {
      console.error('[MUSIC] Error in play/pause:', error);
    }
  };

  const handleSkip = async () => {
    try {
      console.log('[MUSIC] Skipping to next song...');
      await playNextSong();
      fetchQueue();
    } catch (error) {
      console.error('[MUSIC] Skip error:', error);
    }
  };

  const handleDelete = async (queueId) => {
    try {
      console.log('[MUSIC] Deleting song:', queueId);
      await axios.delete(`${API}/music/${queueId}`, { withCredentials: true });
      fetchQueue();
    } catch (error) {
      console.error('[MUSIC] Failed to delete:', error);
    }
  };

  const handleVolumeChange = (e) => {
    try {
      const newVolume = parseInt(e.target.value);
      console.log('[MUSIC] Volume changed to:', newVolume);
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
      
      if (playerRef.current && typeof playerRef.current.setVolume === 'function') {
        try {
          playerRef.current.setVolume(newVolume);
          if (newVolume > 0 && playerRef.current.isMuted()) {
            playerRef.current.unMute();
          }
        } catch (error) {
          console.error('[MUSIC] Error setting volume:', error);
        }
      }
    } catch (error) {
      console.error('[MUSIC] Volume change error:', error);
    }
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    
    try {
      if (isMuted) {
        console.log('[MUSIC] Unmuting...');
        setIsMuted(false);
        setVolume(80);
        playerRef.current.unMute();
        playerRef.current.setVolume(80);
      } else {
        console.log('[MUSIC] Muting...');
        setIsMuted(true);
        playerRef.current.mute();
      }
    } catch (error) {
      console.error('[MUSIC] Error toggling mute:', error);
    }
  };

  const handleAddUrl = async () => {
    if (!newSongUrl.trim()) return;
    
    try {
      setAddingUrl(true);
      console.log('[MUSIC] Adding URL:', newSongUrl);
      
      await axios.post(
        `${API}/music/add`,
        {
          youtube_url: newSongUrl.trim(),
          added_by: 'Admin'
        },
        { withCredentials: true }
      );
      
      // Clear input and refresh queue
      setNewSongUrl('');
      await fetchQueue();
      
      console.log('[MUSIC] URL added successfully');
    } catch (error) {
      console.error('[MUSIC] Failed to add URL:', error);
      alert('Αποτυχία προσθήκης URL. Ελέγξτε ότι είναι έγκυρο YouTube link.');
    } finally {
      setAddingUrl(false);
    }
  };

  const handleUrlKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddUrl();
    }
  };

  const upNextQueue = queue.filter(item => item.status === 'queued').sort((a, b) => a.order - b.order);

  return (
    <>
      <MusicQRModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
      />

      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/95 flex items-end sm:items-center justify-center z-[9999]"
          onClick={onClose}
        >
          <div 
            className="bg-gradient-to-b from-zinc-900 to-black w-full sm:max-w-lg sm:rounded-2xl overflow-hidden sm:m-4 max-h-[100dvh] sm:max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Compact */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/50 shrink-0">
              <div className="flex items-center gap-2">
                <Music className="w-5 h-5 text-green-500" />
                <h2 className="text-lg font-semibold text-white">Music Control</h2>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowQRModal(true)}
                  className="p-2 hover:bg-zinc-800 rounded-full transition-colors"
                  title="Show QR Code"
                  data-testid="show-qr-btn"
                >
                  <QrCode className="w-5 h-5 text-zinc-400" />
                </button>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-zinc-800 rounded-full transition-colors"
                  data-testid="close-music-modal"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
            </div>

            {/* Main Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  {/* YouTube Player - Hidden on Mobile, Visible on Desktop */}
                  <div className="relative bg-black hidden md:block">
                    {nowPlaying ? (
                      <YouTube
                        key={nowPlaying.video_id} // Force re-render when video changes
                        videoId={nowPlaying.video_id}
                        opts={{
                          width: '100%',
                          height: '100%',
                          playerVars: {
                            autoplay: 1,
                            controls: 1,
                            modestbranding: 1,
                            rel: 0,
                            playsinline: 1,
                            fs: 1,
                            enablejsapi: 1
                          }
                        }}
                        onReady={onPlayerReady}
                        onStateChange={onPlayerStateChange}
                        onError={onPlayerError}
                        className="w-full aspect-video"
                        data-testid="youtube-player"
                      />
                    ) : (
                      <div className="w-full aspect-video flex flex-col items-center justify-center bg-zinc-900">
                        <Music className="w-16 h-16 text-zinc-700 mb-3" />
                        <p className="text-zinc-500 text-sm">Δεν παίζει τραγούδι</p>
                        <p className="text-zinc-600 text-xs mt-1">Προσθέστε τραγούδια στην ουρά</p>
                      </div>
                    )}
                  </div>

                  {/* Now Playing Info */}
                  {nowPlaying && (
                    <div className="px-4 py-3 bg-zinc-900/50">
                      <div className="flex items-center gap-3">
                        <img 
                          src={nowPlaying.thumbnail} 
                          alt={nowPlaying.title}
                          className="w-14 h-14 rounded-lg object-cover shadow-lg"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium text-sm leading-tight line-clamp-2">
                            {nowPlaying.title}
                          </div>
                          <div className="text-xs text-zinc-500 mt-1">
                            Προστέθηκε από {nowPlaying.added_by}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Player Controls */}
                  <div className="px-4 py-4 border-b border-zinc-800/50">
                    {/* Main Controls */}
                    <div className="flex items-center justify-center gap-4 mb-4">
                      <button
                        onClick={() => setShuffleMode(!shuffleMode)}
                        className={`p-2 rounded-full transition-all ${
                          shuffleMode 
                            ? 'text-green-500' 
                            : 'text-zinc-500 hover:text-white'
                        }`}
                        title={shuffleMode ? "Shuffle: ON" : "Shuffle: OFF"}
                        data-testid="shuffle-btn"
                      >
                        <Shuffle className="w-5 h-5" />
                      </button>

                      <button
                        onClick={handlePlayPause}
                        className="p-4 bg-white text-black rounded-full hover:scale-105 transition-transform shadow-lg"
                        title={isPlaying ? "Pause" : "Play"}
                        data-testid="play-pause-btn"
                      >
                        {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-0.5" />}
                      </button>

                      <button
                        onClick={handleSkip}
                        className="p-3 bg-zinc-800 text-white rounded-full hover:bg-zinc-700 transition-colors"
                        title="Skip"
                        data-testid="skip-btn"
                      >
                        <SkipForward className="w-6 h-6" />
                      </button>

                      <button
                        onClick={() => setLoopMode(!loopMode)}
                        className={`p-2 rounded-full transition-all ${
                          loopMode 
                            ? 'text-green-500' 
                            : 'text-zinc-500 hover:text-white'
                        }`}
                        title={loopMode ? "Loop: ON" : "Loop: OFF"}
                        data-testid="loop-btn"
                      >
                        <Repeat className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Volume Control */}
                    <div className="flex items-center gap-3">
                      <button onClick={toggleMute} className="text-zinc-400 hover:text-white transition-colors">
                        {isMuted || volume === 0 ? (
                          <VolumeX className="w-5 h-5" />
                        ) : (
                          <Volume2 className="w-5 h-5" />
                        )}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={volume}
                        onChange={handleVolumeChange}
                        className="flex-1 h-1 bg-zinc-700 rounded-full appearance-none cursor-pointer accent-green-500"
                        data-testid="volume-slider"
                      />
                      <span className="text-xs text-zinc-500 w-8 text-right">{volume}%</span>
                    </div>
                  </div>

                  {/* Add Song URL - Admin Only */}
                  <div className="px-4 py-3 border-b border-zinc-800/50">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newSongUrl}
                        onChange={(e) => setNewSongUrl(e.target.value)}
                        onKeyPress={handleUrlKeyPress}
                        placeholder="Προσθήκη YouTube URL..."
                        className="flex-1 bg-zinc-800 text-white text-sm px-3 py-2 rounded-lg border border-zinc-700 focus:border-green-500 focus:outline-none placeholder:text-zinc-500"
                        disabled={addingUrl}
                        data-testid="add-url-input"
                      />
                      <button
                        onClick={handleAddUrl}
                        disabled={!newSongUrl.trim() || addingUrl}
                        className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed"
                        title="Προσθήκη"
                        data-testid="add-url-btn"
                      >
                        {addingUrl ? (
                          <div className="w-5 h-5 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Plus className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-zinc-600 mt-2">
                      Επικολλήστε YouTube URL και πατήστε Enter ή το κουμπί +
                    </p>
                  </div>

                  {/* Up Next Queue */}
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wide">
                        Επόμενα
                      </h3>
                      <span className="text-xs text-zinc-600 bg-zinc-800 px-2 py-0.5 rounded-full">
                        {upNextQueue.length} τραγούδια
                      </span>
                    </div>
                    
                    {upNextQueue.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {upNextQueue.map((item, index) => (
                          <div
                            key={item.queue_id}
                            className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors group"
                            data-testid={`queue-item-${index}`}
                          >
                            <span className="text-zinc-600 text-xs w-5 text-center font-medium">
                              {index + 1}
                            </span>
                            <img
                              src={item.thumbnail}
                              alt={item.title}
                              className="w-10 h-10 rounded object-cover"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-white text-sm truncate">{item.title}</div>
                              <div className="text-xs text-zinc-500">{item.added_by}</div>
                            </div>
                            <button
                              onClick={() => handleDelete(item.queue_id)}
                              className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              title="Διαγραφή"
                              data-testid={`delete-queue-item-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-zinc-600 text-sm">
                        <Music className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Η ουρά είναι άδεια</p>
                        <p className="text-xs mt-1">Σκανάρετε το QR code για να προσθέσετε τραγούδια</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MusicControlModal;
